/**
 * Community Event Detail Page
 * /community/events/:eventId - 일정 상세 페이지
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { Link } from "wouter";
import { ArrowLeft, Calendar, MapPin, Users, Clock, CheckCircle, XCircle, HelpCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { buildLoginPath } from "@/lib/auth-navigation";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Comments } from "@/components/Comments";

interface Event {
  id: number;
  group_id: number;
  title: string;
  description: string | null;
  start_ts: number;
  end_ts: number;
  location_name: string | null;
  location_address: string | null;
  capacity: number | null;
  attendance_count: number;
  maybe_count: number;
  group_title: string;
  group_slug: string;
  attendees: Array<{
    id: number;
    user_uid: string;
    status: string;
    email?: string;
    name?: string;
    avatar_url?: string;
  }>;
}

export default function EventDetailPage() {
  const { user } = useAuth();
  const [, params] = useRoute<{ eventId: string }>("/community/events/:eventId");
  const eventId = params?.eventId ? parseInt(params.eventId) : null;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // 이벤트 상세 조회
  const { data: event, isLoading } = useQuery<Event>({
    queryKey: ["/api/community/events", eventId],
    queryFn: async () => {
      if (!eventId) throw new Error("Invalid event ID");
      const response = await fetch(`/api/community/events/${eventId}`);
      if (!response.ok) throw new Error("Failed to fetch event");
      return response.json();
    },
    enabled: !!eventId,
  });

  // 내 참석 상태 조회
  const { data: myAttendance } = useQuery<{ status: string } | null>({
    queryKey: ["/api/community/events", eventId, "attendance"],
    queryFn: async () => {
      if (!eventId || !user) return null;
      // 이벤트 상세에서 attendees 배열에서 찾기
      const myAttendee = event?.attendees?.find((a) => a.user_uid === user.uid);
      return myAttendee ? { status: myAttendee.status } : null;
    },
    enabled: !!eventId && !!user && !!event,
  });

  // 참석 상태 변경
  const attendMutation = useMutation({
    mutationFn: async (status: "going" | "maybe" | "declined") => {
      if (!user || !eventId) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch(`/api/community/events/${eventId}/attend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "참석 상태 변경에 실패했습니다");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community/events", eventId] });
      queryClient.invalidateQueries({ queryKey: ["/api/community/events", eventId, "attendance"] });
      toast({
        title: "참석 상태가 변경되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <PageContainer size="default">
        <div className="space-y-6">
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
          <div className="h-8 bg-muted rounded w-1/2 animate-pulse" />
        </div>
      </PageContainer>
    );
  }

  if (!event) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">일정을 찾을 수 없습니다</h3>
          <p className="text-muted-foreground mb-4">요청하신 일정이 존재하지 않거나 삭제되었습니다.</p>
          <Button asChild>
            <Link href="/community/groups">모임 목록으로</Link>
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const startDate = new Date(event.start_ts * 1000);
  const endDate = new Date(event.end_ts * 1000);
  const isPast = endDate < new Date();
  const isFull = event.capacity ? event.attendance_count >= event.capacity : false;

  return (
    <>
      <Seo
        title={`${event.title} — OZKNEWS`}
        description={event.description || `${event.group_title}의 일정`}
        path={`/community/events/${event.id}`}
        keywords={[event.group_title, "교민모임", "일정"]}
      />

      <div className="min-h-screen bg-background">
        <PageContainer size="default">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" asChild>
                <Link href={`/community/groups/${event.group_id}`}>
                  <ArrowLeft className="w-4 h-4" />
                </Link>
              </Button>
              <div>
                <Link
                  href={`/community/groups/${event.group_id}`}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {event.group_title}
                </Link>
                <h1 className="text-3xl font-bold">{event.title}</h1>
              </div>
            </div>

            {/* Event Info */}
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Date & Time */}
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-[#663366] dark:text-[#9966cc] mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">일시</h3>
                    <p className="text-muted-foreground">
                      {startDate.toLocaleDateString("ko-KR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        weekday: "long",
                      })}
                    </p>
                    <p className="text-muted-foreground">
                      {startDate.toLocaleTimeString("ko-KR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      - {endDate.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                    {isPast && <Badge variant="secondary" className="mt-2">지난 일정</Badge>}
                  </div>
                </div>

                {/* Location */}
                {event.location_name && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#663366] dark:text-[#9966cc] mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1">장소</h3>
                      <p className="text-muted-foreground">{event.location_name}</p>
                      {event.location_address && (
                        <p className="text-sm text-muted-foreground">{event.location_address}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Attendance */}
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-[#663366] dark:text-[#9966cc] mt-1" />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">참석자</h3>
                    <p className="text-muted-foreground">
                      {event.attendance_count}명 참석
                      {event.capacity && ` / ${event.capacity}명`}
                      {event.maybe_count > 0 && ` (${event.maybe_count}명 가능)`}
                    </p>
                    {isFull && <Badge variant="destructive" className="mt-2">정원 마감</Badge>}
                  </div>
                </div>

                {/* Description */}
                {event.description && (
                  <div>
                    <h3 className="font-semibold mb-2">설명</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">{event.description}</p>
                  </div>
                )}

                {/* Attendance Buttons */}
                {user && !isPast && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      variant={myAttendance?.status === "going" ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => attendMutation.mutate("going")}
                      disabled={attendMutation.isPending || isFull}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      참석
                    </Button>
                    <Button
                      variant={myAttendance?.status === "maybe" ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => attendMutation.mutate("maybe")}
                      disabled={attendMutation.isPending}
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      가능
                    </Button>
                    <Button
                      variant={myAttendance?.status === "declined" ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => attendMutation.mutate("declined")}
                      disabled={attendMutation.isPending}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      불참
                    </Button>
                  </div>
                )}

                {!user && (
                  <div className="pt-4 border-t">
                    <Button asChild className="w-full bg-[#c066ac] hover:bg-[#b055a0]">
                      <Link href={buildLoginPath()}>로그인 후 참석하기</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attendees List */}
            {event.attendees && event.attendees.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">참석자 목록</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {event.attendees.map((attendee) => (
                      <div key={attendee.id} className="flex items-center gap-2">
                        {attendee.avatar_url ? (
                          <img
                            src={attendee.avatar_url}
                            alt={attendee.name || attendee.email}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Users className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {attendee.name || attendee.email || "익명"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Comments Section */}
            <Card>
              <CardContent className="p-6">
                <Comments
                  articleId={String(eventId)}
                  targetType="community_event"
                />
              </CardContent>
            </Card>
          </div>
        </PageContainer>
      </div>
    </>
  );
}
